/* ============================================================
   EM Pack Configurator — runtime
   - State: sessionStorage per pack_id
   - Pack view: qty +/- / remove, recalc en temps réel
   - Customize view: Section Rendering API (search?q=product_type:X[ tag:Y])
   - Add to cart: /cart/add.js avec properties._pack_id
   - Wishlist: wishlists du compte client (fenêtre ERP, window.EMWishlistERP)
   ============================================================ */
(function() {
  'use strict';

  const root = document.querySelector('[data-em-pack]');
  if (!root) return;

  const PACK_ID      = root.dataset.packId;
  const PACK_NAME    = root.dataset.packName;
  const PACK_THEME   = root.dataset.packTheme;
  const PACK_SIZE    = root.dataset.packSize;
  const CURRENCY     = root.dataset.currency || 'EUR';

  // Version key : hash léger des IDs des cartes rendues côté serveur.
  // Si le contenu du pack change (nouveau produit, retag, etc.), la clé
  // sessionStorage change → état précédent invalidé → re-init from DOM.
  const _packCardIds = Array.from(root.querySelectorAll('[data-view="pack"] [data-em-card]'))
    .map(c => c.dataset.productId)
    .sort()
    .join(',');
  let _hash = 5381;
  for (let i = 0; i < _packCardIds.length; i++) {
    _hash = ((_hash << 5) + _hash) ^ _packCardIds.charCodeAt(i);
  }
  const PACK_VERSION = (_hash >>> 0).toString(36);
  const STORAGE_KEY  = 'em_pack_' + PACK_ID + '_v' + PACK_VERSION;

  const money = new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: CURRENCY,
    minimumFractionDigits: 0, maximumFractionDigits: 2
  });
  const formatMoney = cents => money.format(cents / 100);

  /* ===================== STATE ===================== */
  // Reset au chargement : le pack revient à son état initial (rendu Liquid) à chaque F5.
  // SAUF si l'URL contient un hash #em=... avec des modifs sérialisées → on les ré-applique.
  sessionStorage.removeItem(STORAGE_KEY);
  let state = initStateFromDOM();
  // Composition d'origine du pack, figée avant toute modification (2026-10-12)
  const ORIGINE_PACK = JSON.parse(JSON.stringify(state.items));
  // Snapshot des qty initiales (pristine du Liquid) AVANT toute modif URL
  const initialQtyById = {};
  Object.values(state.items).forEach(it => { initialQtyById[it.product_id] = it.qty; });
  // Applique l'état encodé dans l'URL si présent
  applyUrlState();

  function loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }
  // Produits ajoutés ou remplacés : leurs infos sont gardées sur l'appareil
  // pour les remettre dans le pack au rechargement (#em=… ne garde que id~qté)
  // (2026-10-23)
  function lireMemo() { try { return JSON.parse(localStorage.getItem('em_pack_produits_ajoutes') || '{}'); } catch (e) { return {}; } }
  function memoriserAjouts() {
    try {
      const memo = lireMemo();
      Object.values(state.items).forEach(it => {
        if (initialQtyById[it.product_id] === undefined && it.handle) {
          const { qty, ...infos } = it;
          memo[it.product_id] = { ...(memo[it.product_id] || {}), ...infos };
        }
      });
      const ids = Object.keys(memo);
      if (ids.length > 300) ids.slice(0, ids.length - 300).forEach(id => delete memo[id]);
      localStorage.setItem('em_pack_produits_ajoutes', JSON.stringify(memo));
    } catch (e) { /* stockage indisponible : pas de restauration */ }
  }
  function saveState() {
    memoriserAjouts();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    updateUrlState();
    document.dispatchEvent(new CustomEvent('em:pack-state-change'));
  }

  /* URL state : encode les modifs (suppressions + qty diff) dans le hash de l'URL.
     Format : #em=<id>~<qty>,<id>~<qty>,...
     - Seules les qty qui diffèrent du défaut OU les retraits sont encodés.
     - Les retraits sont notés <id>~0
     L'URL est donc compacte et reflète l'état modifié, partageable. */
  function updateUrlState() {
    const initial = initialQtyById;
    const parts = [];
    // Items présents : noter ceux qui ont qty diff du défaut
    Object.values(state.items).forEach(it => {
      const init = initial[it.product_id];
      if (init === undefined) {
        // produit ajouté hors pack initial — noter avec sa qty
        parts.push(it.product_id + '~' + it.qty);
      } else if (init !== it.qty) {
        parts.push(it.product_id + '~' + it.qty);
      }
    });
    // Retraits : produits du pack initial absents du state
    Object.keys(initial).forEach(id => {
      if (!state.items[id]) parts.push(id + '~0');
    });
    const newHash = parts.length ? 'em=' + parts.join(',') : '';
    const cur = location.hash.replace(/^#/, '');
    if (cur !== newHash) {
      const newUrl = location.pathname + location.search + (newHash ? '#' + newHash : '');
      history.replaceState(null, '', newUrl);
    }
  }
  function applyUrlState() {
    if (!location.hash.startsWith('#em=')) return;
    const payload = location.hash.slice(4);
    if (!payload) return;
    const entries = payload.split(',').map(p => p.split('~'));
    const memo = lireMemo();
    entries.forEach(([id, qtyStr]) => {
      const qty = parseInt(qtyStr, 10);
      if (qty <= 0) {
        // retrait
        delete state.items[id];
      } else if (state.items[id]) {
        state.items[id].qty = qty;
      } else if (memo[id]) {
        // Produit ajouté ou remplacé : retrouvé grâce aux infos gardées sur l'appareil
        state.items[id] = { ...memo[id], product_id: id, qty, from_pack: false };
      }
    });
  }
  function initStateFromDOM() {
    const items = {};
    root.querySelectorAll('[data-view="pack"] [data-em-card]').forEach(card => {
      const id = card.dataset.productId;
      items[id] = {
        product_id: id,
        variant_id: card.dataset.variantId,
        handle: card.dataset.productHandle,
        title: card.dataset.productTitle,
        type: card.dataset.productType,
        unit_price: parseInt(card.dataset.unitPrice, 10),
        image: card.dataset.image || (card.querySelector('img') ? card.querySelector('img').src : ''),
        qty: parseInt(card.dataset.qty, 10) || 1,
        mp: card.dataset.mp === '1',
        from_pack: true
      };
    });
    return { pack_id: PACK_ID, pack_name: PACK_NAME, items: items };
  }

  /* ===================== RENDER ===================== */
  // Facteurs déterministes par article (CO2 0,81-0,99 kg / Économies 4,55-6,33 €)
  // Hash sur le product_id pour stabilité au reload + variation entre articles.
  function _impactHashStr(s) {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
    return Math.abs(h);
  }
  function _co2FactorForId(id) {
    return 0.81 + (_impactHashStr(String(id)) % 19) / 100; // 0.81 → 0.99
  }
  function _savingsFactorForId(id) {
    // Hash légèrement différent pour découpler des CO2
    return 4.55 + (_impactHashStr(String(id) + ':s') % 179) / 100; // 4.55 → 6.33
  }

  function totals() {
    let price = 0, count = 0, products = 0, co2 = 0, savings = 0;
    Object.values(state.items).forEach(it => {
      price += it.unit_price * it.qty;
      count += it.qty;
      if (it.qty > 0) products += 1;
      co2 += it.qty * _co2FactorForId(it.product_id);
      savings += it.qty * _savingsFactorForId(it.product_id);
    });
    return { price, count, products, co2, savings };
  }

  // Délai de livraison estimé : 7 jours ouvrés (skip samedi + dimanche)
  function estimateDeliveryDate() {
    const d = new Date();
    let added = 0;
    while (added < 7) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++; // skip dim+sam
    }
    return d;
  }
  function formatDateFr(d) {
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  }
  function renderDelivery() {
    const d = estimateDeliveryDate();
    const txt = formatDateFr(d);
    document.querySelectorAll('[data-em-delivery]').forEach(el => el.textContent = txt);
  }

  function renderTotals() {
    const t = totals();
    root.querySelectorAll('[data-total-price]').forEach(el => el.textContent = formatMoney(t.price));
    root.querySelectorAll('[data-total-count]').forEach(el => el.textContent = t.count);
    root.querySelectorAll('[data-total-products]').forEach(el => el.textContent = t.products);
    // CO2 et économies : virgule + 2 décimales
    const co2Str = t.co2.toFixed(2).replace('.', ',');
    document.querySelectorAll('[data-total-co2]').forEach(el => el.textContent = co2Str);
    const savingsStr = t.savings.toFixed(2).replace('.', ',');
    document.querySelectorAll('[data-total-savings]').forEach(el => el.textContent = savingsStr);
  }

  function findCatGrid(category) {
    // Cherche la cat-section dont le titre matche, retourne sa cat-grid
    const sections = root.querySelectorAll('.em-pack__cat-section');
    for (const sec of sections) {
      const titleEl = sec.querySelector('.em-pack__cat-title');
      if (titleEl && titleEl.textContent.trim() === category) {
        return sec.querySelector('.em-pack__cat-grid');
      }
    }
    return null;
  }

  function handleizeCat(s) {
    return String(s).toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/['‘’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  // Ordre d'insertion attendu (matche cat_order Liquid : Mobilier → Décoration → Équipement)
  const CAT_ORDER = [
    'Assises','Tables','Etagères et rangements','Salon','Chambre','Cuisine','Salle de bain',
    'Mobilier d\'extérieur','Fenêtre',
    'Linge de maison','Décorations murales','Décorations à poser','Luminaires','Plantes artificielles',
    'Petits électros','Gros électros','Accessoires LCD','Produits ménagers',
    'Outillage et Bricolage','Prestations de service'
  ];

  function createCatSection(category) {
    const grid = root.querySelector('[data-pack-grid]');
    if (!grid) return null;
    const handle = handleizeCat(category);
    const sectionHTML = `
      <details class="em-pack__cat-section" id="cat-${handle}" open>
        <summary class="em-pack__cat-heading">
          <span class="em-pack__cat-title">${escapeHTML(category)}</span>
          <span class="em-pack__cat-count">0 produit · 0 article · <strong class="em-pack__cat-total">0,00 €</strong></span>
          <span class="em-pack__cat-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </span>
        </summary>
        <div class="em-pack__cat-grid">
          <button type="button" class="em-pack-card-add" data-action="add-product-modal" data-cat="${escapeHTML(category)}">
            <span class="em-pack-card-add__plus" aria-hidden="true">+</span>
            <span class="em-pack-card-add__title">Ajouter un produit</span>
            <span class="em-pack-card-add__sub">dans « ${escapeHTML(category)} »</span>
          </button>
        </div>
      </details>
    `;
    // Insère à la bonne position selon CAT_ORDER
    const newIdx = CAT_ORDER.indexOf(category);
    const existing = Array.from(grid.querySelectorAll('details.em-pack__cat-section'));
    let inserted = false;
    for (const sec of existing) {
      const t = sec.querySelector('.em-pack__cat-title')?.textContent.trim();
      const idx = CAT_ORDER.indexOf(t);
      if (idx > newIdx) {
        sec.insertAdjacentHTML('beforebegin', sectionHTML);
        inserted = true;
        break;
      }
    }
    if (!inserted) {
      // Insérer avant "Autre" si présent, sinon à la fin
      const autre = document.getElementById('cat-autre');
      if (autre) autre.insertAdjacentHTML('beforebegin', sectionHTML);
      else grid.insertAdjacentHTML('beforeend', sectionHTML);
    }
    return root.querySelector(`#cat-${handle} .em-pack__cat-grid`);
  }

  function updateNavRowAsAdded(category, count) {
    // Met à jour la row du navigateur pour passer en --in avec count
    const row = document.querySelector(`[data-em-cat-nav][data-cat="${cssEscape(category)}"]`);
    if (!row) return;
    row.classList.add('em-pack__complete-row--in');
    row.dataset.inPack = 'true';
    const icon = row.querySelector('.em-pack__complete-row-icon');
    if (icon) icon.textContent = '✓';
    let countEl = row.querySelector('.em-pack__complete-row-count');
    if (!countEl) {
      countEl = document.createElement('span');
      countEl.className = 'em-pack__complete-row-count';
      // Insérer avant la flèche
      const arrow = row.querySelector('.em-pack__complete-row-arrow');
      row.insertBefore(countEl, arrow);
    }
    countEl.textContent = count;
  }

  function cssEscape(s) {
    // Échappe les caractères spéciaux pour CSS selector
    return String(s).replace(/["\\]/g, '\\$&');
  }

  // Cuisine / Salle de bain / Luminaires « - Générique » regroupés avec la catégorie principale (2026-10-23)
  function catRegroupee(t) { if (t === 'Luminaire - Générique') return 'Luminaires'; return /^(Cuisine|Salle de bain) - Générique$/.test(t || '') ? t.replace(' - Générique', '') : t; }

  function insertCardInCatGrid(it) {
    let catGrid = findCatGrid(catRegroupee(it.type));
    if (!catGrid) {
      // Catégorie absente : on crée la section dynamiquement
      catGrid = createCatSection(catRegroupee(it.type));
    }
    if (!catGrid) {
      // Fallback : ajoute à la fin du grid global
      const grid = root.querySelector('[data-pack-grid]');
      if (grid) grid.insertAdjacentHTML('beforeend', packCardHTML(it));
      return;
    }
    // Insère AVANT la tile "+ Ajouter un produit"
    const tile = catGrid.querySelector('.em-pack-card-add');
    if (tile) tile.insertAdjacentHTML('beforebegin', packCardHTML(it));
    else catGrid.insertAdjacentHTML('beforeend', packCardHTML(it));
    // S'assure que la section est ouverte (sinon la nouvelle card ne se voit pas)
    const section = catGrid.closest('details.em-pack__cat-section');
    if (section && !section.open) section.open = true;
    // Met à jour la row du navigateur (si présente)
    const catCount = catGrid.querySelectorAll('[data-em-card]').length;
    updateNavRowAsAdded(catRegroupee(it.type), catCount);
  }

  function renderPackGrid() {
    const grid = root.querySelector('[data-pack-grid]');
    if (!grid) return;
    // Synchronise les cartes existantes avec le state, ajoute celles qui manquent, retire les autres.
    const knownIds = new Set();
    grid.querySelectorAll('[data-em-card]').forEach(card => {
      const id = card.dataset.productId;
      if (!state.items[id]) {
        card.remove();
        return;
      }
      knownIds.add(id);
      updateCardDom(card, state.items[id]);
    });
    Object.values(state.items).forEach(it => {
      if (!knownIds.has(it.product_id)) {
        insertCardInCatGrid(it);
      }
    });
    if (Object.keys(state.items).length === 0) {
      grid.innerHTML = '<div class="em-pack__empty"><p>Votre pack est vide.</p></div>';
    }
    recomputeCatHeaders();
  }

  function recomputeCatHeaders() {
    root.querySelectorAll('.em-pack__cat-section').forEach(sec => {
      const grid = sec.querySelector('.em-pack__cat-grid');
      if (!grid) return;
      const cards = grid.querySelectorAll('[data-em-card]');
      let products = 0, articles = 0, totalCents = 0;
      cards.forEach(c => {
        const qty = parseInt(c.dataset.qty || '0', 10) || 0;
        if (qty <= 0) return;
        products++;
        articles += qty;
        const up = parseInt(c.dataset.unitPrice || '0', 10) || 0;
        totalCents += up * qty;
      });
      const countEl = sec.querySelector('.em-pack__cat-count');
      const totalEl = sec.querySelector('.em-pack__cat-total');
      if (countEl) {
        const pLabel = products > 1 ? 'produits' : 'produit';
        const aLabel = articles > 1 ? 'articles' : 'article';
        const moneyStr = formatMoney(totalCents);
        countEl.innerHTML = `${products} ${pLabel} · ${articles} ${aLabel} · <strong class="em-pack__cat-total">${moneyStr}</strong>`;
      } else if (totalEl) {
        totalEl.textContent = formatMoney(totalCents);
      }
    });
  }

  function updateCardDom(card, item) {
    card.dataset.qty = item.qty;
    card.querySelectorAll('[data-qty-display]').forEach(el => el.textContent = item.qty);
    card.querySelectorAll('[data-qty-display-large]').forEach(el => el.textContent = item.qty);
    const lt = card.querySelector('[data-line-total]');
    if (lt) lt.textContent = formatMoney(item.unit_price * item.qty);
    const up = card.querySelector('[data-unit-price-display]');
    if (up) {
      up.textContent = `soit ${formatMoney(item.unit_price)} / unité`;
      if (item.qty > 1) up.removeAttribute('hidden');
      else up.setAttribute('hidden', '');
    }
  }

  function packCardHTML(it) {
    const safeTitle = escapeHTML(it.title || '');
    const productUrl = it.handle ? `/products/${it.handle}` : '#';
    const img = it.image ? `<img src="${it.image}" alt="${safeTitle}" loading="lazy">` : '<div class="em-pack-card__placeholder"></div>';
    return `
      <article class="em-pack-card" data-em-card
               data-product-id="${it.product_id}" data-variant-id="${it.variant_id}"
               data-product-handle="${it.handle}" data-product-title="${safeTitle}"
               data-product-type="${escapeHTML(it.type || '')}" data-unit-price="${it.unit_price}"
               data-image="${it.image || ''}" data-qty="${it.qty}"${it.mp ? ' data-mp="1"' : ''}>
        <div class="em-pack-card__media">
          <a class="em-pack-card__media-link" href="${productUrl}" target="_blank" rel="noopener" aria-label="Voir ${safeTitle}">${img}</a>
          <div class="em-pack-card__badge" data-qty-display>${it.qty}</div>
          <div class="em-pack-card__controls" aria-hidden="true">
            <button type="button" class="em-pack-card__ctrl" data-action="dec" aria-label="Diminuer">−</button>
            <span class="em-pack-card__ctrl-qty" data-qty-display-large>${it.qty}</span>
            <button type="button" class="em-pack-card__ctrl" data-action="inc" aria-label="Augmenter">+</button>
          </div>
          <button type="button" class="em-pack-card__remove" data-action="remove" aria-label="Retirer">×</button>
        </div>
        <div class="em-pack-card__body">
          ${it.mp ? '<span class="em-pack-card__mp">Marketplace</span>' : ''}
          <h3 class="em-pack-card__title"><a href="${productUrl}" target="_blank" rel="noopener" class="em-pack-card__title-link">${safeTitle}</a></h3>
          <div class="em-pack-card__price" data-line-total>${formatMoney(it.unit_price * it.qty)}</div>
          <div class="em-pack-card__unit-price" data-unit-price-display${it.qty > 1 ? '' : ' hidden'}>soit ${formatMoney(it.unit_price)} / unité</div>
        </div>
      </article>`;
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'
    }[c]));
  }

  /* ===================== ACTIONS ===================== */
  function changeQty(productId, delta) {
    if (!state.items[productId]) return;
    state.items[productId].qty += delta;
    if (state.items[productId].qty <= 0) {
      removeProduct(productId);
      return;
    }
    saveState();
    renderPackGrid();
    renderTotals();
  }
  function removeProduct(productId) {
    delete state.items[productId];
    saveState();
    renderPackGrid();
    renderTotals();
    // Si la vue customize est active, mettre à jour la deco
    if (!root.querySelector('[data-view="customize"]').hidden) {
      decorateCustomizeCards();
    }
  }
  function addProduct(data) {
    const id = String(data.product_id);
    if (state.items[id]) {
      state.items[id].qty += 1;
    } else {
      state.items[id] = {
        product_id: id,
        variant_id: data.variant_id,
        handle: data.handle,
        title: data.title,
        type: data.type,
        unit_price: parseInt(data.unit_price, 10),
        image: data.image || '',
        qty: 1,
        mp: !!data.mp,
        from_pack: false
      };
    }
    saveState();
    renderTotals();
    decorateCustomizeCards();
  }

  /* ===================== VIEW TOGGLE ===================== */
  const packView = root.querySelector('[data-view="pack"]');
  const customizeView = root.querySelector('[data-view="customize"]');
  const toggleLabel = root.querySelector('[data-toggle-label]');

  function toggleView() {
    const goCustomize = !packView.hidden;
    packView.hidden = goCustomize;
    customizeView.hidden = !goCustomize;
    if (toggleLabel) {
      toggleLabel.textContent = goCustomize
        ? "Voir uniquement ce qu'il y a dans le pack"
        : "Personnaliser mon pack";
    }
    if (goCustomize) {
      if (!currentCategory) renderCustomizeFromState();
      else decorateCustomizeCards();
    } else {
      renderPackGrid();
    }
  }

  /* ===================== CUSTOMIZE VIEW (sprint 3) ===================== */
  let currentCategory = null;
  let currentGroup = null;
  let currentPage = 1;
  let totalPages = 1;

  const GROUP_LABELS = {
    theme: 'Mobilier',
    generic: 'Décoration',
    plus: 'Équipement & services'
  };

  function renderCustomizeFromState() {
    const grid = root.querySelector('[data-customize-grid]');
    if (!grid) return;
    const items = Object.values(state.items);
    if (items.length === 0) {
      grid.innerHTML = '<p class="em-pack__hint">Aucun produit dans votre pack. Cliquez sur une catégorie pour explorer le vivier.</p>';
      return;
    }
    grid.innerHTML = items.map(it => customizeCardHTML(it, state.items[it.product_id].qty)).join('');
    setCurrentLabel('Votre pack', items.length + ' produit' + (items.length > 1 ? 's' : ''));
  }

  function customizeCardHTML(it, inPackQty) {
    const safeTitle = escapeHTML(it.title || '');
    const img = it.image ? `<img src="${it.image}" alt="${safeTitle}" loading="lazy">` : '<div class="em-pack-card__placeholder"></div>';
    const inPack = inPackQty > 0;
    return `
      <article class="em-pack-card em-pack-card--customize ${inPack ? 'em-pack-card--in-pack' : ''}"
               data-em-customize-card
               data-product-id="${it.product_id}" data-variant-id="${it.variant_id}"
               data-product-handle="${it.handle}" data-product-title="${safeTitle}"
               data-product-type="${escapeHTML(it.type || '')}" data-unit-price="${it.unit_price}"
               data-image="${it.image || ''}"${it.mp ? ' data-mp="1"' : ''}>
        <div class="em-pack-card__media">
          ${img}
          <div class="em-pack-card__badge ${inPack ? '' : 'em-pack-card__badge--hidden'}" data-in-pack-badge>
            <span data-in-pack-qty>${inPackQty || 0}</span>
          </div>
          <button type="button" class="em-pack-card__add" data-action="add-to-pack" aria-label="Ajouter au pack">+</button>
        </div>
        <div class="em-pack-card__body">
          ${it.mp ? '<span class="em-pack-card__mp">Marketplace</span>' : ''}
          <h3 class="em-pack-card__title">${safeTitle}</h3>
          <div class="em-pack-card__price">${formatMoney(it.unit_price)}</div>
        </div>
      </article>`;
  }

  function setCurrentLabel(group, cat) {
    const g = root.querySelector('[data-current-group]');
    const c = root.querySelector('[data-current-cat]');
    if (g) g.textContent = group;
    if (c) c.textContent = cat;
  }

  async function loadCategoryGrid(cat, group, page = 1) {
    const grid = root.querySelector('[data-customize-grid]');
    if (!grid) return;
    grid.innerHTML = '<p class="em-pack__hint">Chargement…</p>';

    const themeTag = (group === 'theme') ? PACK_THEME : null;
    const qParts = ['product_type:' + cat];
    if (themeTag) qParts.push('tag:' + themeTag);
    const q = qParts.join(' ');
    const url = '/search?q=' + encodeURIComponent(q) + '&type=product&section_id=em-pack-customize-grid&page=' + page;

    try {
      const res = await fetch(url);
      const html = await res.text();
      // Shopify wraps section in <div id="shopify-section-..."><...inner...></div>
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const inner = tmp.firstElementChild ? tmp.firstElementChild.innerHTML : html;
      grid.innerHTML = inner;
      decorateCustomizeCards();
      hookPagination(cat, group);
    } catch (e) {
      grid.innerHTML = '<p class="em-pack__hint">Erreur de chargement. Réessayez.</p>';
    }
  }

  function decorateCustomizeCards() {
    const grid = root.querySelector('[data-customize-grid]');
    if (!grid) return;
    const inPack = [];
    const others = [];
    grid.querySelectorAll('[data-em-customize-card]').forEach(card => {
      const id = card.dataset.productId;
      const item = state.items[id];
      const badge = card.querySelector('[data-in-pack-badge]');
      const qtyEl = card.querySelector('[data-in-pack-qty]');
      if (item && item.qty > 0) {
        card.classList.add('em-pack-card--in-pack');
        if (badge) badge.classList.remove('em-pack-card__badge--hidden');
        if (qtyEl) qtyEl.textContent = item.qty;
        inPack.push(card);
      } else {
        card.classList.remove('em-pack-card--in-pack');
        if (badge) badge.classList.add('em-pack-card__badge--hidden');
        others.push(card);
      }
    });
    // Selected on top
    inPack.concat(others).forEach(card => grid.appendChild(card));
  }

  function hookPagination(cat, group) {
    const grid = root.querySelector('[data-customize-grid]');
    if (!grid) return;
    const nextBtn = grid.querySelector('[data-load-more]');
    if (!nextBtn) return;
    nextBtn.addEventListener('click', async () => {
      const page = parseInt(nextBtn.dataset.nextPage, 10);
      const themeTag = (group === 'theme') ? PACK_THEME : null;
      const qParts = ['product_type:' + cat];
      if (themeTag) qParts.push('tag:' + themeTag);
      const q = qParts.join(' ');
      const url = '/search?q=' + encodeURIComponent(q) + '&type=product&section_id=em-pack-customize-grid&page=' + page;
      nextBtn.disabled = true;
      nextBtn.textContent = 'Chargement…';
      const res = await fetch(url);
      const html = await res.text();
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const inner = tmp.firstElementChild || tmp;
      // Append cards, remove old load-more, append new one if present
      const newCards = inner.querySelectorAll('[data-em-customize-card]');
      newCards.forEach(c => grid.appendChild(c));
      nextBtn.remove();
      const newLoadMore = inner.querySelector('[data-load-more]');
      if (newLoadMore) grid.appendChild(newLoadMore);
      decorateCustomizeCards();
      hookPagination(cat, group);
    });
  }

  /* ===================== PILLS ===================== */
  root.querySelectorAll('[data-pills]').forEach(container => {
    container.addEventListener('click', e => {
      const pill = e.target.closest('.em-pack__pill');
      if (!pill) return;
      const group = container.closest('[data-group]').dataset.group;
      const cat = pill.dataset.cat;

      root.querySelectorAll('.em-pack__pill').forEach(p => p.classList.remove('em-pack__pill--active'));
      pill.classList.add('em-pack__pill--active');

      currentCategory = cat;
      currentGroup = group;
      currentPage = 1;
      setCurrentLabel(GROUP_LABELS[group], cat);
      loadCategoryGrid(cat, group);
    });
  });

  /* ===================== ADD TO CART ===================== */
  async function addPackToCart(btn) {
    const items = Object.values(state.items).filter(i => i.qty > 0);
    if (items.length === 0) { alert('Votre pack est vide.'); return; }
    // Pack modifié par le client (ajout, retrait, quantité) : remonte dans
    // la commande pour le taux de fiabilité de l'ERP (2026-10-12).
    let packModifie = 'non';
    try {
      const origine = ORIGINE_PACK;
      const ids = new Set([...Object.keys(origine), ...items.map(i => String(i.product_id))]);
      ids.forEach(id => {
        const o = origine[id], a = items.find(i => String(i.product_id) === id);
        if (!o || !a || o.qty !== a.qty || a.from_pack === false) packModifie = 'oui';
      });
    } catch (e) { packModifie = ''; }

    const original = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.innerHTML = 'Ajout…'; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: items.map(it => ({
            id: parseInt(it.variant_id, 10),
            quantity: it.qty,
            properties: {
              _pack_id: PACK_ID,
              _pack_name: PACK_NAME,
              _pack_size: PACK_SIZE,
              _pack_theme: PACK_THEME,
              ...(packModifie ? { _pack_modifie: packModifie } : {})
            }
          }))
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert('Erreur ajout panier : ' + (err.description || res.statusText));
        if (btn) { btn.disabled = false; btn.innerHTML = original; }
        return;
      }
      // Vider le state pack (le pack est dans le panier maintenant)
      sessionStorage.removeItem(STORAGE_KEY);
      window.location.href = '/cart';
    } catch (e) {
      alert('Erreur réseau.');
      if (btn) { btn.disabled = false; btn.innerHTML = original; }
    }
  }

  /* ===================== DEVIS (em_quote_products) ===================== */
  function addPackToQuote(btn) {
    const items = Object.values(state.items).filter(i => i.qty > 0);
    if (items.length === 0) { alert('Votre pack est vide.'); return; }
    let quote = [];
    try { quote = JSON.parse(localStorage.getItem('em_quote_products')) || []; } catch (e) {}
    items.forEach(it => {
      const existing = quote.findIndex(x => x.product_id === String(it.product_id));
      const entry = {
        product_id: String(it.product_id),
        variant_id: String(it.variant_id),
        handle: it.handle,
        title: it.title,
        type: it.type || '',
        unit_price: parseInt(it.unit_price, 10),
        image: it.image,
        qty: it.qty,
        savedAt: Math.floor(Date.now() / 1000),
        _pack_id: PACK_ID,
        _pack_name: PACK_NAME
      };
      if (existing >= 0) quote[existing].qty = (quote[existing].qty || 1) + it.qty;
      else quote.push(entry);
    });
    localStorage.setItem('em_quote_products', JSON.stringify(quote));
    window.dispatchEvent(new Event('storage'));
    if (btn) {
      const original = btn.innerHTML;
      btn.innerHTML = '<span>✓ ' + items.length + ' produit' + (items.length>1?'s':'') + ' ajoutés au devis</span>';
      btn.disabled = true;
      setTimeout(() => { btn.innerHTML = original; btn.disabled = false; }, 2200);
    }
  }

  function addPackToWishlistProducts(btn) {
    const items = Object.values(state.items).filter(i => i.qty > 0);
    if (items.length === 0) { alert('Votre pack est vide.'); return; }

    // Mode wishlist actif (outils.moood) ?
    if (window.EMWishlistMode && window.EMWishlistMode.isActive()) {
      const payload = items.map(it => ({
        product_id: String(it.product_id),
        variant_id: String(it.variant_id),
        handle: it.handle,
        title: it.title,
        type: it.type || '',
        unit_price: parseInt(it.unit_price, 10),
        image: it.image,
        qty: parseInt(it.qty, 10) || 1,
      }));
      if (btn) { btn.disabled = true; const orig = btn.innerHTML; btn.innerHTML = '<span>Ajout…</span>';
        window.EMWishlistMode.postItems(payload).then((res) => {
          btn.disabled = false;
          if (res.ok) {
            btn.innerHTML = '<span>♥ ' + payload.length + ' ajoutés à la wishlist</span>';
            window.EMWishlistMode.toast(`♥ ${payload.length} produit${payload.length > 1 ? 's' : ''} ajouté${payload.length > 1 ? 's' : ''} à la wishlist`);
          } else {
            btn.innerHTML = orig;
            window.EMWishlistMode.toast(`Erreur : ${res.error || 'inconnue'}`, 'error');
          }
          setTimeout(() => { btn.innerHTML = orig; }, 2400);
        });
      }
      return;
    }

    // Mode normal : wishlists du compte client (fenêtre ERP, plusieurs produits), 2026-10-08.
    // Plus d'écriture dans la wishlist locale. Ouverture synchrone au clic.
    if (window.EMWishlistERP) {
      const first = items.find(it => it.image);
      window.EMWishlistERP.openList(
        items.map(it => ({ handle: it.handle, variant_id: String(it.variant_id), qty: parseInt(it.qty, 10) || 1 })),
        PACK_NAME || 'Mon pack',
        first ? first.image : ''
      );
    }
  }

  /* WISHLIST locale (legacy, clé 'em_wishlist') supprimée le 2026-10-08 : tout passe par les wishlists du compte client. */

  /* ===================== EVENT DELEGATION ===================== */
  root.addEventListener('click', function(e) {
    const t = e.target.closest('[data-action]');
    if (!t) return;
    const action = t.dataset.action;
    const card = t.closest('[data-em-card], [data-em-customize-card]');

    switch (action) {
      case 'inc':
        if (card) changeQty(card.dataset.productId, 1);
        break;
      case 'dec':
        if (card) changeQty(card.dataset.productId, -1);
        break;
      case 'remove':
        if (card) removeProduct(card.dataset.productId);
        break;
      case 'add-to-pack':
        if (card) {
          addProduct({
            product_id: card.dataset.productId,
            variant_id: card.dataset.variantId,
            handle: card.dataset.productHandle,
            title: card.dataset.productTitle,
            type: card.dataset.productType,
            unit_price: parseInt(card.dataset.unitPrice, 10),
            image: card.dataset.image,
            mp: card.dataset.mp === '1'
          });
        }
        break;
      case 'toggle-view':
        toggleView();
        break;
      case 'add-product':
        toggleView();
        break;
      case 'add-to-cart':
        addPackToCart(t);
        break;
      case 'wishlist':
        addPackToWishlistProducts(t);
        break;
      case 'quote':
        addPackToQuote(t);
        break;
    }
  });

  /* ===================== NAVIGATEUR CATÉGORIES (Compléter votre pack) ===================== */
  document.addEventListener('click', (e) => {
    const row = e.target.closest('[data-em-cat-nav]');
    if (!row) return;
    e.preventDefault();
    e.stopPropagation();
    const inPack = row.dataset.inPack === 'true';
    if (inPack) {
      // Scroll vers la section dans le pack
      const target = row.dataset.target;
      const el = document.getElementById(target);
      if (!el) return;
      if (el.tagName === 'DETAILS' && !el.open) el.open = true;
      const y = el.getBoundingClientRect().top + window.scrollY - 24;
      window.scrollTo({ top: y, behavior: 'smooth' });
    } else {
      // Ouvre le modal Ajouter sur cette catégorie
      const cat = row.dataset.cat;
      if (cat) document.dispatchEvent(new CustomEvent('em:open-add-modal', { detail: { cat } }));
    }
  });

  /* ===================== CHIPS FILTRES RAPIDES (legacy) ===================== */
  const chipsNav = root.querySelector('[data-pack-chips]');
  if (chipsNav) {
    const chips = chipsNav.querySelectorAll('.em-pack__chip');

    function setActive(chip) {
      chips.forEach(c => c.classList.remove('em-pack__chip--active'));
      chip.classList.add('em-pack__chip--active');
    }

    chips.forEach(chip => {
      chip.addEventListener('click', () => {
        const target = chip.dataset.target;
        setActive(chip);
        if (target === 'all') {
          const top = root.getBoundingClientRect().top + window.scrollY - 20;
          window.scrollTo({ top, behavior: 'smooth' });
        } else {
          const el = document.getElementById(target);
          if (!el) return;
          if (el.tagName === 'DETAILS' && !el.open) el.open = true;
          const y = el.getBoundingClientRect().top + window.scrollY - 24;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
    });

    // Highlight chip de la catégorie visible (IntersectionObserver)
    const sections = root.querySelectorAll('.em-pack__cat-section[id]');
    if (sections.length && 'IntersectionObserver' in window) {
      const map = new Map();
      chips.forEach(c => map.set(c.dataset.target, c));
      const io = new IntersectionObserver((entries) => {
        // Trouve la section la plus visible parmi celles entrées
        let bestEntry = null;
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!bestEntry || entry.intersectionRatio > bestEntry.intersectionRatio) bestEntry = entry;
          }
        }
        if (bestEntry) {
          const chip = map.get(bestEntry.target.id);
          if (chip) setActive(chip);
        }
      }, { rootMargin: '-100px 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] });
      sections.forEach(s => io.observe(s));
    }
  }

  /* ===================== ADD PRODUCT FROM MODAL ===================== */
  // Quand l'utilisateur clique "+ Ajouter au pack" dans le modal "Ajouter un produit"
  document.addEventListener('em:add-product-from-modal', (e) => {
    const it = e.detail || {};
    const id = String(it.product_id);
    if (!id) return;
    if (state.items[id]) {
      // Déjà dans le pack : +1 qty
      state.items[id].qty += 1;
    } else {
      state.items[id] = {
        product_id: id,
        variant_id: it.variant_id,
        handle: it.handle,
        title: it.title,
        type: it.type,
        unit_price: parseInt(it.unit_price, 10) || 0,
        image: it.image,
        qty: 1,
        mp: !!it.mp,
        from_pack: false,
      };
    }
    renderPackGrid();
    renderTotals();
    saveState();
  });

  // Pastille « Alternatives » (em-pack-alternatives.js, 2026-10-23) : le
  // produit choisi remplace l'ancien, avec la même quantité.
  document.addEventListener('em:replace-product', (e) => {
    const ancien = String(e.detail?.old_id || '');
    const it = e.detail?.item || {};
    const id = String(it.product_id || '');
    if (!ancien || !id || !state.items[ancien]) return;
    const qty = state.items[ancien].qty || 1;
    // Remplacé = même origine que l'ancien, pour revenir à sa place au rechargement
    const remplace = state.items[ancien].remplace || ancien;
    delete state.items[ancien];
    if (state.items[id]) state.items[id].qty += qty;
    else {
      state.items[id] = { product_id: id, variant_id: it.variant_id, handle: it.handle, title: it.title, type: it.type,
        unit_price: parseInt(it.unit_price, 10) || 0, image: it.image, qty, mp: !!it.mp, from_pack: false, remplace };
      // La nouvelle carte prend la place de l'ancienne
      const vieille = root.querySelector(`[data-view="pack"] [data-em-card][data-product-id="${cssEscape(ancien)}"]`);
      if (vieille) {
        vieille.insertAdjacentHTML('beforebegin', packCardHTML(state.items[id]));
        if (vieille.previousElementSibling) vieille.previousElementSibling.dataset.subcat = vieille.dataset.subcat || '';
      }
    }
    renderPackGrid();
    renderTotals();
    saveState();
  });

  // Bouton « − » de la fenêtre « Ajouter un produit » (2026-10-20)
  document.addEventListener('em:change-qty-from-modal', (e) => {
    const id = String(e.detail?.product_id || '');
    if (id) changeQty(id, Number(e.detail?.delta) || 0);
  });

  /* ===================== THEME SWITCHER : prefetch au hover ===================== */
  const _prefetched = new Set();
  document.querySelectorAll('[data-theme-prefetch]').forEach(link => {
    const onHover = () => {
      const href = link.getAttribute('href');
      if (!href || _prefetched.has(href)) return;
      _prefetched.add(href);
      const l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      l.as = 'document';
      document.head.appendChild(l);
    };
    link.addEventListener('mouseenter', onHover, { once: true });
    link.addEventListener('touchstart', onHover, { once: true, passive: true });
    link.addEventListener('focus', onHover, { once: true });
  });

  /* ===================== ORDRE FIXE DANS CHAQUE CATÉGORIE ===================== */
  // Même ordre dans tous les packs (Sébastien 2026-10-23) : table à manger,
  // puis table basse… Les produits ajoutés par le client restent à la suite.
  const ORDRE_SOUS_CATS = [
    'Canapé', 'Fauteuil', 'Chaise', 'Tabouret',
    'Table à manger', 'Table basse', 'Bureau', 'Table de chevet', 'Console',
    'Meuble TV', 'Buffet', 'Bibliothèque', 'Étagère', 'Commode',
    'Sommier', 'Matelas', 'Armoire', 'Couette', 'Oreiller', 'Drap housse',
    'Suspension', 'Lampadaire', 'Lampe à poser',
    'Miroir', 'Tableau', 'Horloge', 'Étagère murale', 'Patère',
  ];
  function ordonnerCategories() {
    const rang = (sc) => { const i = ORDRE_SOUS_CATS.indexOf(sc); return i < 0 ? ORDRE_SOUS_CATS.length : i; };
    root.querySelectorAll('[data-view="pack"] .em-pack__cat-grid').forEach(grid => {
      const cartes = Array.from(grid.querySelectorAll(':scope > [data-em-card]'));
      // Produits du pack (et leurs remplaçants) triés ; ajouts du client à la suite, dans leur ordre
      const pack = cartes.filter(c => c.dataset.subcat !== undefined);
      const ajouts = cartes.filter(c => c.dataset.subcat === undefined);
      pack.sort((a, b) => rang(a.dataset.subcat) - rang(b.dataset.subcat)
        || String(a.dataset.subcat).localeCompare(String(b.dataset.subcat), 'fr')
        || String(a.dataset.productTitle).localeCompare(String(b.dataset.productTitle), 'fr'));
      const tuile = grid.querySelector(':scope > .em-pack-card-add');
      [...pack, ...ajouts].forEach(c => grid.insertBefore(c, tuile));
    });
  }

  /* ===================== INIT ===================== */
  Object.values(state.items).forEach(it => {
    if (!it.remplace || initialQtyById[it.product_id] !== undefined) return;
    const origine = root.querySelector(`[data-view="pack"] [data-em-card][data-product-id="${cssEscape(String(it.remplace))}"]`);
    if (origine && !root.querySelector(`[data-view="pack"] [data-em-card][data-product-id="${cssEscape(it.product_id)}"]`)) {
      origine.insertAdjacentHTML('beforebegin', packCardHTML(it));
      if (origine.previousElementSibling) origine.previousElementSibling.dataset.subcat = origine.dataset.subcat || '';
    }
  });
  ordonnerCategories();
  renderPackGrid();
  renderTotals();
  renderDelivery();
  saveState();
})();
